import { useSelector } from 'react-redux';
import { DefaultRootState } from '@reduxLocal/persistState';
import { NavigationRouteContext } from '@react-navigation/native';
import { useContext, useMemo } from 'react';
import { Platform } from 'react-native';
import { userDataIndicatesPaidSubscription } from '@utils/subscriptionBillingGuard';

/**
 * Hook to check if the current user is in learning mode
 * Learning mode restricts access to paid features
 *
 * Safe outside a NavigationContainer (e.g. App root sync components) —
 * route-based checks are skipped when no route context exists.
 *
 * @returns {boolean} True if user is in learning mode
 */
export const useLearningMode = (): boolean => {
  // Prefer context over useRoute() so callers outside a navigator do not throw
  const route = useContext(NavigationRouteContext) as
    | { name?: string; params?: { learningMode?: boolean } }
    | undefined;
  const userState = useSelector((state: DefaultRootState) => state.promiseData?.data);
  const learningModeState = useSelector((state: DefaultRootState) =>
    state.app?.learningMode || state.promiseData?.learningMode
  );

  return useMemo(() => {
    const isLearnRoute = route?.name === 'Learn';
    const hasLearningParam = route?.params?.learningMode === true;

    let hasUrlParam = false;
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search);
      hasUrlParam =
        urlParams.get('learning') === 'true' ||
        urlParams.get('learn') === 'true' ||
        window.location.pathname.includes('/learn');
    }

    const isLearningAccount =
      userState?.userData?.accountType === 'learning' ||
      userState?.userData?.isLearningAccount === true;

    return (
      isLearnRoute ||
      hasLearningParam ||
      hasUrlParam ||
      isLearningAccount ||
      learningModeState === true
    );
  }, [route?.name, route?.params, userState, learningModeState]);
};

/**
 * Hook to check if user can access paid features
 * Returns false if user is in learning mode
 * 
 * @returns {boolean} True if user can access paid features
 */
export const useCanAccessPaidFeatures = (): boolean => {
  const isLearningMode = useLearningMode();
  const hasActiveSubscription = useSelector((state: DefaultRootState) => {
    const user = state.promiseData?.data?.userData;
    if (!user) return false;
    
    // Check multiple premium indicators
    const isPremium = user?.isPremium === true;
    const hasActiveSubscription = user?.subscriptionStatus === 'active';
    const hasBillingActive = userDataIndicatesPaidSubscription(user);
    
    return isPremium || hasActiveSubscription || hasBillingActive;
  });
  
  // If in learning mode, always return false (no paid features)
  if (isLearningMode) {
    return false;
  }
  
  return hasActiveSubscription;
};

export default useLearningMode;

